import { lumi } from "@/lib/lumi"
import type { AppNotification, DirectMessage, Profile, Purchase, SiteSetting, WaitlistEmail } from "@/types"

type PrivacyPayload = Record<string, unknown>

function unwrap(raw: unknown): unknown {
  if (!raw || typeof raw !== "object") return raw
  const record = raw as Record<string, unknown>
  if (record.data && typeof record.data === "object") return record.data
  return raw
}

export async function callPrivacy<T>(action: string, body: PrivacyPayload = {}): Promise<T> {
  try {
    const functions = (lumi as { functions?: { invoke: (name: string, init?: unknown) => Promise<unknown> } }).functions
    if (!functions?.invoke) throw new Error("Privacy service is unavailable.")
    const raw = await functions.invoke("privacy", {
      method: "POST",
      body: { action, ...body },
    })
    const payload = unwrap(raw) as T & { error?: string }
    if (payload && typeof payload === "object" && payload.error) {
      throw new Error(payload.error)
    }
    return payload
  } catch (error: unknown) {
    const err = error as { data?: { error?: string }; message?: string }
    throw new Error(err?.data?.error || err?.message || "An unknown error occurred")
  }
}

export async function privacyInbox() {
  const res = await callPrivacy<{ list?: DirectMessage[] }>("inbox")
  return (res?.list || []) as DirectMessage[]
}

export async function privacyThread(otherUserId: string) {
  const res = await callPrivacy<{ list?: DirectMessage[] }>("thread", { otherUserId })
  return (res?.list || []) as DirectMessage[]
}

export async function privacySendMessage(recipientId: string, text: string) {
  const res = await callPrivacy<{ item?: DirectMessage }>("sendMessage", { recipientId, text })
  return res?.item as DirectMessage | undefined
}

export async function privacyNotifications() {
  const res = await callPrivacy<{ list?: AppNotification[] }>("notifications")
  return (res?.list || []) as AppNotification[]
}

export async function privacyMarkNotificationsRead() {
  await callPrivacy("markNotificationsRead")
}

export async function privacyNotify(input: {
  recipientId?: string
  type: AppNotification["type"]
  postId?: string
  commentId?: string
  text?: string
}) {
  await callPrivacy("notify", input)
}

export async function privacyUpdatePost(postId: string, kind: string, extra: PrivacyPayload = {}) {
  await callPrivacy("updatePost", { postId, kind, ...extra })
}

export async function privacyCompletePurchase(postId: string, txHash: string, fromWallet: string) {
  await callPrivacy("completePurchase", { postId, txHash, fromWallet })
}

export async function privacyListWaitlist() {
  const res = await callPrivacy<{ list?: WaitlistEmail[] }>("listWaitlist")
  return (res?.list || []) as WaitlistEmail[]
}

export async function privacySetComingSoon(enabled: boolean) {
  const res = await callPrivacy<{ setting?: SiteSetting }>("setComingSoon", { enabled })
  return (res?.setting || null) as SiteSetting | null
}

export async function privacyAdminOverview() {
  const res = await callPrivacy<{ setting?: SiteSetting; waitlist?: WaitlistEmail[]; reports?: Array<Record<string, unknown>> }>("adminOverview")
  return {
    setting: (res?.setting || null) as SiteSetting | null,
    waitlist: (res?.waitlist || []) as WaitlistEmail[],
    reports: res?.reports || [],
  }
}

export async function privacyPublicProfiles(options: { userIds?: string[]; username?: string; suggested?: boolean } = {}) {
  const res = await callPrivacy<{ list?: Profile[] }>("publicProfiles", options)
  return (res?.list || []) as Profile[]
}

export async function privacyMyPurchases() {
  const res = await callPrivacy<{ list?: Purchase[] }>("myPurchases")
  return (res?.list || []) as Purchase[]
}

export async function privacyLookupWallet(wallet: string) {
  const res = await callPrivacy<{ userId?: string }>("lookupWallet", { wallet })
  return String(res?.userId || "")
}
